import BaseStore from 'stores/BaseStore';
import { makeAutoObservable, runInAction, when } from 'mobx';
import { RootStoreT } from 'types/frontend/RootStore';
import { FeedItemT } from 'types/shared/core/Feeds';
import { api } from 'utils/api/api';
import UserStore from 'stores/root/UserStore';
import { FeedsResponseT } from 'types/shared/reqAndRes/Feeds';
import {
  EmptyRequestT,
  NewStatusRequestT,
} from 'types/shared/reqAndRes/CommonRequest';
import { MyResponse } from 'types/shared/reqAndRes/MyResponse';
import {
  DO_UPDATE_POST_REVIEW_STATUS,
  READ_FEED,
  READ_REVIEW_FEEDS,
} from 'network/shared/post';
import { PostReviewStatusT, PostT, PostTypeT } from 'types/shared/core/Post';
import interfaceBridge from 'utils/interfaceBridge';
import { MyError } from 'utils/MyError';
import { ErrorCode } from 'constants/shared/ErrorCode';
import PostActionStore from 'stores/PostActionStore';

export type PostReviewScopeT = 'inbox' | 'mine';

export default class TeacherPostReviewListStore implements BaseStore {
  feedsOf: Record<PostReviewScopeT, FeedItemT[]> = {
    inbox: [],
    mine: [],
  };

  isFetching: Record<PostReviewScopeT, boolean> = {
    inbox: false,
    mine: false,
  };

  userStore: UserStore;
  postActionStore: PostActionStore;

  constructor(rootStore: RootStoreT) {
    this.userStore = rootStore.userStore;
    this.postActionStore = new PostActionStore(
      this.userStore,
      this.editPostCallback,
      this.togglePostVisibilityCallback,
      this.deletePostCallback,
    );

    makeAutoObservable(this);
  }

  init = (scope: PostReviewScopeT) => {
    console.log('init');
    when(() => !!this.userStore.myInfo).then(() => {
      if (scope === 'inbox' && !this.userStore.isManager) {
        throw new Error('invalid permission');
      }
      this.loadReviewFeeds(scope);
    });
  };

  loadReviewFeeds = async (scope: PostReviewScopeT) => {
    if (this.isFetching[scope]) return;
    this.isFetching[scope] = true;

    try {
      const result = await api<EmptyRequestT, MyResponse<FeedsResponseT>>(
        READ_REVIEW_FEEDS(0, scope),
        {},
      );

      console.log('loadReviewFeeds result', result);

      if (result.isSuccess && result.object) {
        const feedsResponse = result.object as FeedsResponseT;

        this.replaceFeeds(scope, feedsResponse.feeds);
      } else {
        await interfaceBridge.customAlert(
          '글 목록 불러오기 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
      this.isFetching[scope] = false;
    } catch (e) {
      await MyError.handleCatchedError(e);
      this.isFetching[scope] = false;
    }
  };

  editPostCallback = (post: PostT) => {
    console.log('editPostCallback');
    this.updateSingleFeed(post.type, post.id);
  };

  updateSingleFeed = async (postType: PostTypeT, postId: number) => {
    try {
      const result = await api<EmptyRequestT, MyResponse<FeedItemT>>(
        READ_FEED(0, postType, postId),
        {},
      );

      console.log('loadFeed result', result);

      runInAction(() => {
        if (result.isSuccess && result.object) {
          for (const scope of ['inbox', 'mine']) {
            const index = this.feedsOf[scope].findIndex(
              (feed) => feed.type === 'postFeed' && feed.content.id === postId,
            );
            if (index !== -1) {
              this.feedsOf[scope][index] = result.object;
              console.log(
                'tag length',
                this.feedsOf[scope][index].content.postTags?.length,
              );
            }
          }
        } else if (!result.isSuccess) {
          interfaceBridge.customAlert(
            '게시글 조회 실패',
            `오류가 발생했습니다. (${result.errorCode})`,
            '닫기',
          );
        }
      });
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };

  togglePostVisibilityCallback = (post: PostT) => {
    // nothing to do
  };

  deletePostCallback = (post: PostT) => {
    this.removePostFeed(post.type, post.id);
  };

  removePostFeed = (postType: PostTypeT, postId: number) => {
    for (const scope of ['inbox', 'mine']) {
      const index = this.feedsOf[scope].findIndex(
        (feed) =>
          feed.type === 'postFeed' &&
          feed.content.type === postType &&
          feed.content.id === postId,
      );
      if (index !== -1) {
        this.feedsOf[scope].splice(index, 1);
      }
    }
  };

  replaceFeeds = (scope: PostReviewScopeT, newFeeds: FeedItemT[]) => {
    this.feedsOf[scope] = newFeeds;
  };

  handleFeedItemClick = (scope: PostReviewScopeT, feedItem: FeedItemT) => {
    if (feedItem.type === 'postFeed') {
      const postFeedItem = feedItem.content;
      interfaceBridge.nav(
        '발행 전 글 보기',
        `/teacherSide/post/review/${scope}/${postFeedItem.type}/${postFeedItem.id}`,
        'current',
        true,
        () => {
          // 다녀오면 다시 목록을 불러옴. 승인/반려 했을 수 있으므로
          this.loadReviewFeeds(scope);
        },
      );
    }
  };

  handlePostCornerAction = (post: PostT) => {
    this.postActionStore.handlePostCornerAction(post);
  };

  approve = async (post: PostT) => {
    this.updatePostReviewStatus(post, 'approved');
  };

  reject = async (post: PostT) => {
    this.updatePostReviewStatus(post, 'rejected');
  };

  updatePostReviewStatus = async (
    post: PostT,
    newStatus: PostReviewStatusT,
  ) => {
    console.log(`post ${post.id} old status = `, post.reviewStatus);
    console.log(`post ${post.id} new status = `, newStatus);

    try {
      const result = await api<
        NewStatusRequestT<PostReviewStatusT>,
        MyResponse<boolean>
      >(DO_UPDATE_POST_REVIEW_STATUS(0, post.type, post.id), {
        newStatus,
      });
      console.log('result', result);
      if (result.isSuccess) {
        runInAction(() => {
          if (newStatus === 'approved') {
            this.removePostFeed(post.type, post.id);
          } else {
            post.reviewStatus = newStatus;
          }
        });
      } else if (result.errorCode === ErrorCode.POST_REVIEW_ALREADY_PUBLISHED) {
        await interfaceBridge.customAlert(
          '승인/반려 실패',
          '이미 발행된 글입니다.',
          '닫기',
        );

        // 이미 삭제
        // 누가 이미 승인
      } else if (!result.isSuccess) {
        await interfaceBridge.customAlert(
          '승인/반려 실패',
          `오류가 발생했습니다. (${result.errorCode})`,
          '닫기',
        );
      }
    } catch (e) {
      await MyError.handleCatchedError(e);
    }
  };
}
